import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { useToast } from "vue-toastification";
import companyService from "@/services/company";

export const useCompanyStore = defineStore("company", () => {
  const toast = useToast();
  const loading = ref(false);

  // Reactive company context — single source of truth for the current company.
  // Initialised from localStorage; updated on company switch so all subscribers
  // re-render without a page reload.
  const _companyId = ref(localStorage.getItem("currentCompanyId") || "");
  const _tableName = ref(localStorage.getItem("tableName") || "");

  const companyId = computed(() => _companyId.value);
  const tableName = computed(() => _tableName.value);

  function setCurrentCompany(id, tName = "") {
    _companyId.value = id;
    localStorage.setItem("currentCompanyId", id);
    if (tName) {
      _tableName.value = tName;
      localStorage.setItem("tableName", tName);
    }
  }

  async function getCompanySettings(id) {
    loading.value = true;
    try {
      const res = await companyService.getCompanySettings(id);
      return res;
    } catch (err) {
      toast.error(err?.data?.message);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function updateCompanySettings(payload) {
    loading.value = true;
    try {
      const res = await companyService.updateCompanySettings(payload);
      toast.success(res.message);
      return res;
    } catch (err) {
      toast.error(err?.data?.message);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function editCompany(payload) {
    loading.value = true;
    try {
      const res = await companyService.editCompany(payload);
      toast.success(res.message);
      return res;
    } catch (err) {
      toast.error(err?.data?.message);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function manageSubscription(payload) {
    loading.value = true;
    try {
      const res = await companyService.generateManageSubscriptionUrl(payload);
      return res;
    } catch (err) {
      toast.error(err?.data?.message);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function generateToken(id) {
    loading.value = true;
    try {
      const res = await companyService.generateToken(id);
      return res;
    } catch (err) {
      toast.error(err?.data?.message);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function fetchSubscriptionInfo(id) {
    loading.value = true;
    try {
      const res = await companyService.getSubscriptionInfo(id);
      return res;
    } catch (err) {
      toast.error(err?.data?.message);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function getAllCompanyApps(id) {
    loading.value = true;
    try {
      const res = await companyService.getAllCompanyApps(id);
      return res;
    } catch (err) {
      toast.error(err?.data?.message);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  return {
    loading,
    companyId,
    tableName,
    setCurrentCompany,
    getCompanySettings,
    updateCompanySettings,
    editCompany,
    manageSubscription,
    generateToken,
    fetchSubscriptionInfo,
    getAllCompanyApps,
  };
});
